import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Series } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/bill-utils";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  CalendarClock,
  Pencil,
  Save,
  X,
  AlertTriangle,
  Trash2,
  ChevronRight,
} from "lucide-react";

const TYPE_LABEL = { recurring: "Recurring", plan: "Payment plan" };
const FREQ_LABEL = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

const SeriesManager = ({ bill, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [scope, setScope] = useState("future_unpaid");
  const [form, setForm] = useState({
    company: "",
    amount_due: "",
    notes: "",
    email: "",
    phone: "",
    bank_name: "",
    bsb: "",
    account_number: "",
    account_name: "",
    payment_reference: "",
  });
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const seriesId = bill?.series_id;

  const load = async () => {
    if (!seriesId) return;
    setLoading(true);
    try {
      const d = await Series.get(seriesId);
      setData(d);
      setForm({
        company: d.company || "",
        amount_due: bill.amount_due ?? "",
        notes: bill.notes || "",
        email: bill.email || "",
        phone: bill.phone || "",
        bank_name: bill.bank_name || "",
        bsb: bill.bsb || "",
        account_number: bill.account_number || "",
        account_name: bill.account_name || "",
        payment_reference: bill.payment_reference || "",
      });
    } catch {
      toast.error("Could not load series");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    // Only send fields that the user actually changed (non-empty)
    const payload = { scope };
    for (const [k, v] of Object.entries(form)) {
      if (k === "amount_due") {
        const n = Number(v);
        if (!Number.isNaN(n) && n > 0) payload.amount_due = n;
        continue;
      }
      if (typeof v === "string" && v.trim() !== "") payload[k] = v.trim();
    }
    if (Object.keys(payload).length <= 1) {
      toast.error("Nothing to save — change at least one field");
      return;
    }
    setSaving(true);
    try {
      const res = await Series.update(seriesId, payload);
      toast.success(`Updated ${res.updated_count} bill${res.updated_count === 1 ? "" : "s"}`);
      setEditing(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not update series",
      );
    } finally {
      setSaving(false);
    }
  };

  const cancelSeries = async (deleteScope) => {
    const msg =
      deleteScope === "all"
        ? `Delete ALL ${data.bills.length} bills in this series (including paid history)? This cannot be undone.`
        : `Cancel ${data.bills.filter((b) => (b.remaining_balance || 0) > 0).length} unpaid future bill${data.bills.filter((b) => (b.remaining_balance || 0) > 0).length === 1 ? "" : "s"}? Paid bills will be kept. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setCancelling(true);
    try {
      const res = await Series.cancel(seriesId, deleteScope);
      toast.success(`Removed ${res.deleted_count} bill${res.deleted_count === 1 ? "" : "s"}`);
      if (deleteScope === "all" || data.bills.every((b) => (b.remaining_balance || 0) > 0)) {
        // Whole series gone — go back to dashboard
        window.location.href = "/";
        return;
      }
      await load();
      onChanged && onChanged();
    } catch {
      toast.error("Could not cancel series");
    } finally {
      setCancelling(false);
    }
  };

  if (!seriesId) return null;

  const typeLabel = TYPE_LABEL[bill.series_type] || "Series";
  const freqLabel = FREQ_LABEL[bill.series_frequency] || bill.series_frequency || "";

  return (
    <section
      data-testid="series-manager"
      className="swiss-card border-l-4 border-l-purple-500"
    >
      <button
        data-testid="series-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-3">
          <CalendarClock className="h-5 w-5 text-purple-600" />
          <div>
            <div className="swiss-label">{typeLabel} series</div>
            <div className="font-display font-extrabold text-lg leading-tight">
              {bill.series_index} of {bill.series_total}
              {freqLabel ? ` · ${freqLabel}` : ""}
            </div>
          </div>
        </div>
        <ChevronRight
          className={`h-5 w-5 text-neutral-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="mt-4 pt-4 border-t border-neutral-200 space-y-5">
          {loading && (
            <div className="text-sm text-neutral-500">Loading series…</div>
          )}

          {data && (
            <>
              {/* Action buttons */}
              {!editing && (
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="series-edit-btn"
                    onClick={() => setEditing(true)}
                    className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit series
                  </button>
                  <button
                    data-testid="series-cancel-future-btn"
                    onClick={() => cancelSeries("future_unpaid")}
                    disabled={cancelling}
                    className="border-2 border-red-500 text-red-600 font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-red-500 hover:text-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Cancel unpaid
                  </button>
                  <button
                    data-testid="series-cancel-all-btn"
                    onClick={() => cancelSeries("all")}
                    disabled={cancelling}
                    className="border-2 border-neutral-300 text-neutral-600 font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:border-red-500 hover:text-red-600 transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete entire series
                  </button>
                </div>
              )}

              {/* Edit form */}
              {editing && (
                <div
                  data-testid="series-edit-form"
                  className="border-2 border-purple-300 bg-purple-50/40 p-4 space-y-4"
                >
                  <div className="flex items-start gap-2 text-sm text-purple-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      Edits apply to{" "}
                      <span className="font-bold">
                        every bill in this series
                      </span>{" "}
                      that matches the scope below. Leave a field blank to keep
                      its current value. Paid history is never touched.
                    </div>
                  </div>

                  {/* Scope toggle */}
                  <div>
                    <div className="swiss-label mb-2">Apply to</div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        data-testid="series-scope-future"
                        onClick={() => setScope("future_unpaid")}
                        className={`border-2 px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                          scope === "future_unpaid"
                            ? "border-black bg-black text-white"
                            : "border-neutral-300 text-neutral-700 hover:border-black"
                        }`}
                      >
                        Unpaid bills only
                      </button>
                      <button
                        data-testid="series-scope-all"
                        onClick={() => setScope("all")}
                        className={`border-2 px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                          scope === "all"
                            ? "border-black bg-black text-white"
                            : "border-neutral-300 text-neutral-700 hover:border-black"
                        }`}
                      >
                        Every bill in series
                      </button>
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field
                      label="Company"
                      testid="series-field-company"
                      value={form.company}
                      onChange={(v) => setForm({ ...form, company: v })}
                    />
                    <Field
                      label="Amount due (each)"
                      testid="series-field-amount"
                      value={form.amount_due}
                      inputMode="decimal"
                      onChange={(v) => setForm({ ...form, amount_due: v })}
                    />
                    <Field
                      label="Email"
                      testid="series-field-email"
                      value={form.email}
                      onChange={(v) => setForm({ ...form, email: v })}
                    />
                    <Field
                      label="Phone"
                      testid="series-field-phone"
                      value={form.phone}
                      onChange={(v) => setForm({ ...form, phone: v })}
                    />
                    <Field
                      label="Bank name"
                      testid="series-field-bank-name"
                      value={form.bank_name}
                      onChange={(v) => setForm({ ...form, bank_name: v })}
                    />
                    <Field
                      label="BSB"
                      testid="series-field-bsb"
                      value={form.bsb}
                      onChange={(v) => setForm({ ...form, bsb: v })}
                    />
                    <Field
                      label="Account number"
                      testid="series-field-account-number"
                      value={form.account_number}
                      onChange={(v) =>
                        setForm({ ...form, account_number: v })
                      }
                    />
                    <Field
                      label="Account name"
                      testid="series-field-account-name"
                      value={form.account_name}
                      onChange={(v) => setForm({ ...form, account_name: v })}
                    />
                    <Field
                      label="Payment reference"
                      testid="series-field-payment-reference"
                      value={form.payment_reference}
                      onChange={(v) =>
                        setForm({ ...form, payment_reference: v })
                      }
                    />
                  </div>
                  <div>
                    <div className="swiss-label mb-1">Notes</div>
                    <textarea
                      data-testid="series-field-notes"
                      value={form.notes}
                      onChange={(e) =>
                        setForm({ ...form, notes: e.target.value })
                      }
                      rows={2}
                      className="swiss-input w-full"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    <button
                      data-testid="series-save-btn"
                      onClick={save}
                      disabled={saving}
                      className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                    >
                      <Save className="h-3.5 w-3.5" />
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      className="border-2 border-neutral-300 text-neutral-700 font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:border-black hover:text-black transition-colors inline-flex items-center gap-2"
                    >
                      <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Series list */}
              <div>
                <div className="swiss-label mb-2">
                  All bills in this series ({data.bills.length})
                </div>
                <ul className="divide-y divide-neutral-100 border border-neutral-200">
                  {data.bills.map((b) => {
                    const isCurrent = b.id === bill.id;
                    return (
                      <li
                        key={b.id}
                        className={`p-3 flex items-center justify-between gap-3 text-sm ${isCurrent ? "bg-yellow-50" : ""}`}
                      >
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/bills/${b.id}`}
                            className="font-display font-bold hover:underline truncate block"
                          >
                            #{b.series_index}/{b.series_total} ·{" "}
                            <span className="hash">{b.invoice_number}</span>
                            {isCurrent && (
                              <span className="ml-2 text-[10px] uppercase tracking-widest text-yellow-700 font-bold">
                                Viewing
                              </span>
                            )}
                          </Link>
                          <div className="text-xs text-neutral-500 truncate">
                            due {fmtDate(b.due_date)} · status {b.status}
                          </div>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <div className="font-display font-extrabold mono tabular-nums">
                            {fmtMoney(b.amount_due)}
                          </div>
                          <div
                            className={`text-[10px] uppercase tracking-widest font-bold ${
                              (b.remaining_balance || 0) > 0
                                ? "text-red-700"
                                : "text-green-700"
                            }`}
                          >
                            {(b.remaining_balance || 0) > 0
                              ? `${fmtMoney(b.remaining_balance)} owed`
                              : "Paid"}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

const Field = ({ label, value, onChange, inputMode, testid }) => (
  <div>
    <div className="swiss-label mb-1">{label}</div>
    <Input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputMode={inputMode}
      className="swiss-input"
    />
  </div>
);

export default SeriesManager;
